import type { DashboardsDashboard, DashboardsWidgetConfig } from '@smarthome/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getWidgetDefinition } from '../registry';
import type { EditorDocument, EditorWidget, WidgetType } from '../types';
import { GRID_COLUMNS } from '../viewer/layout';

/**
 * Generate a unique ID for a widget
 */
function generateWidgetId(): string {
  return `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Convert API widget to editor widget with required fields
 */
function toEditorWidget(widget: {
  id?: string;
  type?: string;
  title?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  config?: DashboardsWidgetConfig;
}): EditorWidget | null {
  // Validate widget type
  const type = widget.type as WidgetType;
  const def = getWidgetDefinition(type);
  if (!def) return null;

  return {
    id: widget.id || generateWidgetId(),
    type,
    title: widget.title || def.displayName,
    x: widget.x ?? 0,
    y: widget.y ?? 0,
    w: widget.w ?? def.defaultSize.w,
    h: widget.h ?? def.defaultSize.h,
    config: widget.config ?? def.defaultConfig,
  };
}

/**
 * Convert API document to editor document
 */
function toEditorDocument(dashboard: DashboardsDashboard): EditorDocument {
  const doc = dashboard.document ?? {};
  const widgets = (doc.widgets ?? [])
    .map(toEditorWidget)
    .filter((w): w is EditorWidget => w !== null);

  return {
    schemaVersion: doc.schemaVersion ?? 1,
    timeRange: doc.timeRange,
    widgets,
  };
}

/**
 * Find first available position for a widget
 */
function findAvailablePosition(
  widgets: EditorWidget[],
  w: number,
  h: number
): { x: number; y: number } {
  // Build occupancy grid
  const maxY = widgets.reduce((max, widget) => Math.max(max, widget.y + widget.h), 0);
  const grid: boolean[][] = [];

  for (let row = 0; row <= maxY + h; row++) {
    grid[row] = new Array(GRID_COLUMNS).fill(false);
  }

  // Mark occupied cells
  for (const widget of widgets) {
    for (let row = widget.y; row < widget.y + widget.h; row++) {
      for (let col = widget.x; col < widget.x + widget.w; col++) {
        const gridRow = grid[row];
        if (gridRow) {
          gridRow[col] = true;
        }
      }
    }
  }

  // Find first available position
  for (let row = 0; row <= maxY + h; row++) {
    for (let col = 0; col <= GRID_COLUMNS - w; col++) {
      let fits = true;
      for (let dy = 0; dy < h && fits; dy++) {
        for (let dx = 0; dx < w && fits; dx++) {
          if (grid[row + dy]?.[col + dx]) {
            fits = false;
          }
        }
      }
      if (fits) {
        return { x: col, y: row };
      }
    }
  }

  // Fallback: place at bottom
  return { x: 0, y: maxY };
}

export interface UseDashboardDraftResult {
  /** Current draft document */
  draft: EditorDocument;
  /** Whether draft has unsaved changes */
  isDirty: boolean;
  /** Add a new widget */
  addWidget: (type: WidgetType, title: string, config: DashboardsWidgetConfig) => void;
  /** Update widget position/size */
  updateWidgetLayout: (id: string, x: number, y: number, w: number, h: number) => void;
  /** Update widget title */
  updateWidgetTitle: (id: string, title: string) => void;
  /** Update widget config */
  updateWidgetConfig: (id: string, config: DashboardsWidgetConfig) => void;
  /** Delete a widget */
  deleteWidget: (id: string) => void;
  /** Reset draft to original */
  resetDraft: () => void;
  /** Replace entire draft document */
  setDocument: (document: EditorDocument) => void;
  /** Update dashboard time range */
  updateTimeRange: (timeRange: string) => void;
  /** Get the document for saving */
  getDocument: () => EditorDocument;
}

/**
 * Hook for managing dashboard draft state
 */
export function useDashboardDraft(dashboard: DashboardsDashboard): UseDashboardDraftResult {
  const initialDocument = useMemo(() => toEditorDocument(dashboard), [dashboard]);
  const [draft, setDraft] = useState<EditorDocument>(initialDocument);
  const originalRef = useRef<EditorDocument>(initialDocument);

  // Update when dashboard changes (e.g., after save)
  useEffect(() => {
    const newDoc = toEditorDocument(dashboard);
    originalRef.current = newDoc;
    setDraft(newDoc);
  }, [dashboard]);

  // Calculate dirty state by comparing with original
  const isDirty = useMemo(() => {
    return JSON.stringify(draft) !== JSON.stringify(originalRef.current);
  }, [draft]);

  const addWidget = useCallback(
    (type: WidgetType, title: string, config: DashboardsWidgetConfig) => {
      const def = getWidgetDefinition(type);
      if (!def) return;

      setDraft((prev) => {
        const position = findAvailablePosition(prev.widgets, def.defaultSize.w, def.defaultSize.h);
        const newWidget: EditorWidget = {
          id: generateWidgetId(),
          type,
          title,
          x: position.x,
          y: position.y,
          w: def.defaultSize.w,
          h: def.defaultSize.h,
          config,
        };

        return {
          ...prev,
          widgets: [...prev.widgets, newWidget],
        };
      });
    },
    []
  );

  const updateWidgetLayout = useCallback(
    (id: string, x: number, y: number, w: number, h: number) => {
      setDraft((prev) => ({
        ...prev,
        widgets: prev.widgets.map((widget) =>
          widget.id === id ? { ...widget, x, y, w, h } : widget
        ),
      }));
    },
    []
  );

  const updateWidgetTitle = useCallback((id: string, title: string) => {
    setDraft((prev) => ({
      ...prev,
      widgets: prev.widgets.map((widget) => (widget.id === id ? { ...widget, title } : widget)),
    }));
  }, []);

  const updateWidgetConfig = useCallback((id: string, config: DashboardsWidgetConfig) => {
    setDraft((prev) => ({
      ...prev,
      widgets: prev.widgets.map((widget) => (widget.id === id ? { ...widget, config } : widget)),
    }));
  }, []);

  const deleteWidget = useCallback((id: string) => {
    setDraft((prev) => ({
      ...prev,
      widgets: prev.widgets.filter((widget) => widget.id !== id),
    }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraft(originalRef.current);
  }, []);

  const updateTimeRange = useCallback((timeRange: string) => {
    setDraft((prev) => ({ ...prev, timeRange }));
  }, []);

  const setDocument = useCallback((document: EditorDocument) => {
    setDraft(document);
  }, []);

  const getDocument = useCallback(() => draft, [draft]);

  return {
    draft,
    isDirty,
    addWidget,
    updateWidgetLayout,
    updateWidgetTitle,
    updateWidgetConfig,
    deleteWidget,
    resetDraft,
    updateTimeRange,
    setDocument,
    getDocument,
  };
}
